// node_modules
import { faFileAlt } from "@fortawesome/free-regular-svg-icons";
import { faBookmark, faInfo, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FC, useContext, useEffect } from "react";
import { Redirect, Route, Switch, useRouteMatch } from "react-router-dom";
// Contexts
import { DeliveryLayoutProvider, SavedQueryProvider, SideNotesProvider, TopicModellingSearchProvider, UserContext, WebsocketContext, WebsocketProvider } from "Providers";
import { AuthContext, CaseContext } from "Stores";
// Components
import { AddCommentTile, AsynchronousWrapper, DocumentSearchTile, ExploreResultsTile, OrganizationSearchTile, QuestionAnswerTile, SquareTabContainer, TileSidebarMenu, TitledChild, TitledIconChild, VoteOrRateTile } from "Components";
import { Intake } from "Components/Case/Intake/Intake";
import { ConceptTechnologyList } from "Components/Case/Results/ConceptTechnologyList/ConceptTechnologyList";
import { Results } from "Components/Case/Results/Results";
import { SidebarMenu } from "Components/Case/SidebarMenu/SidebarMenu";
import { Information } from "../Information/Information";
import { Search, SearchQueryElementHitCounts } from "../Search";
import { DynamicDeliveryTrial } from "./DynamicDeliveryTrial";
// Types
import { TConceptTechnologyListDTO, TUseFetch } from "Types";
// Custom hooks
import { useFetch } from "Hooks";
// Enums
import { CaseActionTypeEnum } from "Enums";
// Interfaces
import { ISetCaseConceptTechnologyListAction } from "Interfaces";
// Constants
import { FeatureToggleConstants, LoggingConstants } from "Constants";
import { TopicModellingResults } from "../Search/TopicModellingResults/TopicModellingResults";
// styles
import { PendoHelperSingleton } from "Helpers";
import styles from "./delivery.module.scss";

export const Delivery: FC = () => {
    // Const
    // `url` lets us build relative links.
    const { url } = useRouteMatch();

    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);
    const { authState } = useContext(AuthContext);
    const { isCurrentUserFindest } = useContext(UserContext);
    const { webSocketController } = useContext(WebsocketContext);
    
    // We use `useFetch` custom hook here
    // to fetch data from the server
    const { isLoading, didError, errorMessage, fetchedData }: TUseFetch<TConceptTechnologyListDTO> = useFetch(`api/cases/${caseState.selectedCaseId}/conceptlist/${caseState.casePhase.toLowerCase()}`);

    // Logic
    useEffect(() => {
        if (caseState.selectedCaseId) {
            webSocketController.invokeFunction("SubscribeToCaseUpdates", caseState.selectedCaseId);
        }
    }, [webSocketController, caseState.selectedCaseId]);

    // Logging
    useEffect(() => {
        PendoHelperSingleton.trackEventWithProperties(`${LoggingConstants.DYNAMIC_DELIVERY}CaseConceptList`, {
            caseId: caseState.selectedCaseId,
            phase: caseState.casePhase.toLowerCase()
        });
    }, [caseState.casePhase, caseState.selectedCaseId]);

    useEffect(() => {
        if (fetchedData) {
            caseDispatch({ type: CaseActionTypeEnum.SetCaseConceptTechnologyList, caseConceptTechnologyList: fetchedData } as ISetCaseConceptTechnologyListAction);
        }
    }, [caseDispatch, fetchedData]);

    return (
        <AsynchronousWrapper didError={didError} errorMessage={errorMessage} isLoading={isLoading} LoaderComponent={null}>
            <DynamicDeliveryTrial />
            <DeliveryLayoutProvider>
                <WebsocketProvider>
                    <SavedQueryProvider>
                        <TopicModellingSearchProvider>
                            <Switch>
                                <SidebarMenu>
                                    <TitledIconChild title="Intake" icon={faFileAlt} />
                                    {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ? 
                                            <TitledIconChild title="Search" icon={faSearch}>
                                                {isCurrentUserFindest ?
                                                    <SquareTabContainer
                                                        extraClassNameMainContainer={styles.topicModellingMainContainer}
                                                        extraClassNameTabContainer={styles.topicModellingTabContainer}
                                                        extraClassNameTab={styles.topicModellingTab}
                                                        extraClassNameTabContentContainer={styles.topicModellingTabContentContainer}
                                                    >
                                                        <TitledChild title="Search terms">
                                                            <SearchQueryElementHitCounts />
                                                        </TitledChild>
                                                            <TitledChild title="Clusters">
                                                                <TopicModellingResults />
                                                            </TitledChild>
                                                    </SquareTabContainer>
                                                    : <SearchQueryElementHitCounts /> }
                                            </TitledIconChild>
                                        : 
                                            null
                                    }
                                    <TitledIconChild title="Results" icon={faBookmark} isResults={true}>
                                        <Route path={`${url}/results`} >
                                            <ConceptTechnologyList isAdditionalResultsEnabled={authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery} />
                                        </Route>
                                    </TitledIconChild>
                                    {FeatureToggleConstants.InformationSection && isCurrentUserFindest ? 
                                        <TitledIconChild title="Information" icon={faInfo} />
                                        : null }
                                </SidebarMenu>
                            </Switch>
                            <SideNotesProvider>
                                <div style={{ width: "100%", overflow: "auto" }}>
                                    <Switch>
                                        <Route path={`${url}/intake`}>
                                            <Intake isEdit={false}/>
                                        </Route>
                                        {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                            <Route path={`${url}/search/:searchType`}>
                                                <Search />
                                            </Route>
                                            : null}
                                        {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                            <Route path={`${url}/search`}>
                                                <Redirect to={`${url}/search/documents`} />
                                            </Route>
                                            : null}
                                        <Route path={`${url}/results`}>
                                            <Results />
                                        </Route>
                                        {FeatureToggleConstants.InformationSection && isCurrentUserFindest ?
                                            <Route path={`${url}/information`}>
                                                <Information />
                                            </Route>
                                            : null }
                                        <Route>
                                            <Redirect to={`${url}/intake`} />
                                        </Route>
                                    </Switch>
                                </div>
                                {caseState.selectedConceptId ?
                                    null
                                    :
                                    <Switch>
                                        <TileSidebarMenu>
                                            <Route path={`${url}/intake`}>
                                                <QuestionAnswerTile />
                                                <AddCommentTile />
                                                {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                                    <>
                                                        <DocumentSearchTile />
                                                        <OrganizationSearchTile />
                                                    </>
                                                    :
                                                    null
                                                }
                                                <ExploreResultsTile />
                                            </Route>
                                            {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                                <Route path={`${url}/search/:searchType`}>
                                                    <ExploreResultsTile />
                                                </Route>
                                                :
                                                null
                                            }
                                            <Route path={`${url}/results`}>
                                                <QuestionAnswerTile />
                                                {caseState.selectedTechnologyId ?
                                                    <VoteOrRateTile /> : null}
                                                <AddCommentTile />
                                                {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                                    <>
                                                        <DocumentSearchTile />
                                                        <OrganizationSearchTile />
                                                    </>
                                                    :
                                                    null
                                                }
                                            </Route>
                                            {authState.hasAccessToDynamicDelivery && caseState.isDynamicDelivery ?
                                                <Route path={`${url}/information`}>
                                                    <DocumentSearchTile />
                                                    <OrganizationSearchTile />
                                                    <ExploreResultsTile />
                                                </Route>
                                            :
                                            null}
                                        </TileSidebarMenu>
                                    </Switch>
                                }
                            </SideNotesProvider>
                        </TopicModellingSearchProvider>
                    </SavedQueryProvider>
                </WebsocketProvider>
            </DeliveryLayoutProvider>
        </AsynchronousWrapper>
    );
};